export const ARTICLES = [
  {
    slug: 'quickbooks-online',
    title: 'Linking to QuickBooks Online',
    intro:
      'Integrating InteriorFolio with QuickBooks Online helps streamline invoicing, purchase orders, and financial workflows between your projects and accounting system. InteriorFolio was created by a team with extensive experience working alongside interior designers in both project management and accounting operations.',
    blocks: [
      {
        type: 'step',
        title: 'Step 1: Open Settings',
        text: 'From your Dashboard, click your profile icon in the upper-right corner and select Settings.',
        image: '/support-images/0.jpg',
      },
      {
        type: 'step',
        title: 'Step 2: Select the QuickBooks Tab',
        text: 'Inside Settings, click the QuickBooks tab to open your QuickBooks integration settings.',
        image: '/support-images/1.png',
      },
      {
        type: 'step',
        title: 'Step 3: Connect to QuickBooks',
        text: "Click Connect to QuickBooks to begin linking your existing QuickBooks Online account. You'll be prompted to:",
        bullets: [
          'Sign into QuickBooks Online',
          "Select the company file you'd like to connect",
          'Approve the integration permissions',
        ],
        note: 'Once connected, InteriorFolio will sync with your selected QuickBooks Online account.',
        image: '/support-images/2.jpg',
      },
      {
        type: 'step',
        title: 'Step 4: Select Your Liability Account',
        text: 'After connecting, make sure to choose your preferred Liability Account from the dropdown menu. This account is used for:',
        bullets: [
          'Client retainers',
          'Customer deposits',
          'Liability tracking workflows',
        ],
      },
      {
        type: 'callout',
        title: "You're Ready to Go",
        text: 'Once connected, your InteriorFolio projects and accounting workflows will work seamlessly alongside QuickBooks Online.',
      },
    ],
  },

  {
    slug: 'creating-proposals',
    title: 'Creating Proposals',
    intro:
      'InteriorFolio was built by accountants who work closely with interior designers every day, which means proposals were designed to support both the creative presentation and the financial workflow behind the scenes.',
    blocks: [
      {
        type: 'step',
        title: 'Step 1: Open Your Project',
        text: 'After creating a project, navigate to the project and select the Proposals tab.',
        image: '/support-images/3.png',
      },
      {
        type: 'step',
        title: 'Step 2: Select "Add New"',
        text: 'Click the Add New button to begin creating a new proposal.',
        image: '/support-images/4.png',
      },
      {
        type: 'step',
        title: 'Step 3: Review Project Information',
        text: 'Before adding items, double-check that all project information is accurate, including:',
        bullets: [
          'Project Site',
          'Billing Address',
          'Customer Name',
          'Contact Information',
        ],
        note: 'This information will automatically populate throughout the proposal workflow.',
      },
      {
        type: 'step',
        title: 'Step 4: Add Items From Your Catalog',
        text: 'Browse or search your InteriorFolio product catalog to begin adding items to the proposal. You can:',
        bullets: [
          'Search by product name',
          'Filter by item type',
          'Quickly add products directly into the proposal',
        ],
        image: '/support-images/5.jpg',
      },
      {
        type: 'step',
        title: 'Step 5: Customize Item Details',
        text: 'Once an item has been added, you can update and customize:',
        bullets: [
          'Trade Price',
          'Selling Price',
          'Quantity',
          'Markup',
          'Shipping',
          'Freight Destination',
          'Client-facing descriptions',
        ],
        note: 'This flexibility allows each proposal to be tailored to the project while keeping pricing organized and transparent.',
        image: '/support-images/6.png',
      },
      {
        type: 'step',
        title: 'Step 6: Assign Rooms',
        text: 'Products can also be assigned to rooms such as:',
        bullets: [
          'Living Room',
          'Primary Bedroom',
          'Kitchen',
          'Guest Bathroom',
        ],
        note: 'These room assignments tie directly into your product library and help keep projects organized.',
        image: '/support-images/7.png',
      },
      {
        type: 'step',
        title: 'Step 7: Add Proposal Tags',
        text: 'Need additional organization? Add tags to your proposal. Examples may include:',
        bullets: [
          'Living Room',
          'New Construction',
          'Guest Suite',
          'Procurement Phase',
        ],
        note: 'Tags can be pre-created and managed within your Settings page.',
        image: '/support-images/8.jpg',
      },
      {
        type: 'step',
        title: 'Step 8: Save Your Proposal',
        text: 'Once your proposal is complete, select:',
        bullets: [
          'Save Draft — to continue editing later',
          'Save & Close — to finalize and exit the proposal',
        ],
        note: 'Your proposal will then appear within the project proposal list for future edits, approvals, and tracking.',
        image: '/support-images/9.jpg',
      },
    ],
  },

  {
    slug: 'creating-invoices',
    title: 'Creating Invoices',
    intro:
      'InteriorFolio offers two ways to create invoices: convert an existing proposal directly into an invoice, or create one manually from scratch.',
    blocks: [
      {
        type: 'section',
        title: 'Option 1: Convert an Existing Proposal into an Invoice',
      },
      {
        type: 'step',
        title: 'Step 1: Open the Proposal',
        text: 'Navigate to your project and select the Proposals tab.',
        image: '/support-images/10.png',
      },
      {
        type: 'step',
        title: 'Step 2: Select Your Proposal',
        text: 'Open the proposal you would like to invoice.',
      },
      {
        type: 'step',
        title: 'Step 3: Select "Convert to Invoice"',
        text: 'From the proposal Actions menu, select Convert to Invoice, then choose which items to include. Items you leave unselected stay on the proposal (marked Partially Invoiced) so you can invoice them later. For the items you select, InteriorFolio will automatically pull over:',
        bullets: [
          'Customer information',
          'Project details',
          'Product selections',
          'Pricing',
          'Quantities',
          'Markups',
          'Shipping details',
          'Room assignments',
        ],
        note: 'This helps eliminate duplicate data entry and keeps your proposal and invoice workflows connected.',
        image: '/support-images/11.jpg',
      },
      {
        type: 'step',
        title: 'Step 4: Review Invoice Details',
        text: 'Before finalizing the invoice, review:',
        bullets: [
          'Pricing',
          'Shipping',
          'Tax settings',
          'Client-facing descriptions',
          'Quantities',
          'Markups',
        ],
        note: 'Make any necessary edits or updates.',
      },
      {
        type: 'step',
        title: 'Step 5: Save or Finalize',
        text: 'Once completed, select Save & Close to finalize the invoice.',
      },
      {
        type: 'section',
        title: 'Option 2: Create a New Invoice Manually',
      },
      {
        type: 'step',
        title: 'Step 1: Open Your Project',
        text: 'Navigate to the appropriate project and select the Invoices tab.',
        image: '/support-images/12.png',
      },
      {
        type: 'step',
        title: 'Step 2: Select "Add New"',
        text: 'Click the Add New button to begin creating a new invoice.',
        image: '/support-images/13.png',
      },
      {
        type: 'step',
        title: 'Step 3: Review Project Information',
        text: 'Before adding invoice items, confirm that all project and client information is accurate, including:',
        bullets: [
          'Project Site',
          'Billing Address',
          'Customer Name',
          'Contact Information',
        ],
        image: '/support-images/14.png',
      },
      {
        type: 'step',
        title: 'Step 4: Add Items From Your Catalog',
        text: 'Search and select products or services directly from your InteriorFolio catalog. You can:',
        bullets: [
          'Search by product name',
          'Filter by item type',
          'Add products directly from your product library',
        ],
        image: '/support-images/15.jpg',
      },
      {
        type: 'step',
        title: 'Step 5: Customize Invoice Details',
        text: 'Once items are added, you can update:',
        bullets: [
          'Quantity',
          'Selling Price',
          'Trade Price',
          'Markup',
          'Shipping',
          'Freight Destination',
          'Client-facing descriptions',
        ],
        image: '/support-images/16.png',
      },
      {
        type: 'step',
        title: 'Step 6: Assign Rooms',
        text: 'Items can also be assigned to rooms such as:',
        bullets: [
          'Living Room',
          'Kitchen',
          'Primary Bedroom',
          'Guest Bathroom',
        ],
        note: 'Room assignments help organize invoices and tie directly into your product library and project structure.',
        image: '/support-images/17.png',
      },
      {
        type: 'step',
        title: 'Step 7: Add Tags',
        text: 'Need additional organization? Add tags to your invoice. Examples may include:',
        bullets: [
          'Procurement',
          'Install Phase',
          'New Construction',
          'Final Delivery',
        ],
        note: 'Tags can be managed within your Settings page.',
        image: '/support-images/18.jpg',
      },
      {
        type: 'step',
        title: 'Step 8: Review Invoice Totals',
        text: 'InteriorFolio will automatically calculate:',
        bullets: ['Subtotal', 'Sales Tax', 'Total Amount Due'],
      },
      {
        type: 'step',
        title: 'Step 9: Save or Finalize',
        text: 'Once completed, select:',
        bullets: [
          'Save Draft — to continue editing later',
          'Save & Close — to finalize the invoice',
        ],
        image: '/support-images/19.png',
      },
      {
        type: 'callout',
        title: 'QuickBooks Integration',
        text: 'If connected to QuickBooks Online, invoices can automatically sync between InteriorFolio and QuickBooks, helping streamline accounting workflows and reduce duplicate data entry.',
      },
    ],
  },

  {
    slug: 'creating-purchase-orders',
    title: 'Creating Purchase Orders',
    intro:
      'InteriorFolio allows you to create purchase orders from scratch or convert an existing proposal or invoice directly into a purchase order.',
    blocks: [
      {
        type: 'section',
        title: 'Option 1: Create From Scratch',
      },
      {
        type: 'step',
        title: 'Step 1: Navigate to Purchase Orders',
        text: 'Open your project and select the Purchase Orders tab.',
        image: '/support-images/20.jpg',
      },
      {
        type: 'step',
        title: 'Step 2: Select "Add New"',
        text: 'Click the Add New button to begin creating a new purchase order.',
        image: '/support-images/21.jpg',
      },
      {
        type: 'step',
        title: 'Step 3: Enter Vendor Information',
        text: 'Fill in all pertinent vendor information gathered from the vendor quote, invoice, or trade portal, including:',
        bullets: [
          'Vendor Name',
          'Phone Number',
          'Email Address',
          'Vendor Address',
          'Tracking URL',
          'Order Status',
          'Sidemark',
        ],
        image: '/support-images/22.png',
      },
      {
        type: 'step',
        title: 'Step 4: Add Payment & Delivery Terms',
        text: 'Enter important procurement details such as:',
        bullets: [
          'Vendor Payment Terms',
          'Deposit Due Date',
          'Balance Due Date',
          'Estimated Delivery Date',
          'Confirmed Ship Date',
          'Actual Delivery Date',
        ],
        note: 'These fields help keep procurement timelines organized and visible throughout the project lifecycle.',
      },
      {
        type: 'step',
        title: 'Step 5: Add Items From Your Catalog',
        text: 'Search and select items directly from your InteriorFolio product catalog. You can:',
        bullets: [
          'Search by item name',
          'Filter by product type',
          'Quickly add products to the purchase order',
        ],
        image: '/support-images/23.jpg',
      },
      {
        type: 'step',
        title: 'Step 6: Update Product Details',
        text: 'Once items are added, update any additional information including:',
        bullets: [
          'Descriptions',
          'Freight details',
          'Quantities',
          'Pricing',
          'Vendor notes',
        ],
        note: 'This allows purchase orders to remain customized and vendor-ready.',
        image: '/support-images/24.jpg',
      },
      {
        type: 'step',
        title: 'Step 7: Add Notes & Tags',
        text: 'Need additional organization? Add:',
        bullets: [
          'Internal Notes',
          'Procurement Notes',
          'Project Tags',
          'Room Tags',
        ],
        note: 'Tags help organize purchase orders across projects and procurement phases.',
        image: '/support-images/25.jpg',
      },
      {
        type: 'step',
        title: 'Step 8: Review Amounts',
        text: 'InteriorFolio will automatically calculate:',
        bullets: [
          'Tax Rate',
          'Taxes',
          'Freight',
          'Payments',
          'Deposit Requested',
          'Subtotal',
          'Total Balance',
        ],
        note: 'This helps keep procurement financials organized and transparent.',
        image: '/support-images/26.jpg',
      },
      {
        type: 'step',
        title: 'Step 9: Save or Sync',
        text: 'Once complete, you can:',
        bullets: [
          'Save Draft',
          'Save & Close',
          'Sync QuickBooks Bill',
          'Download PDF',
        ],
        note: 'If connected to QuickBooks Online, purchase orders can sync directly into QuickBooks as bills.',
      },
      {
        type: 'section',
        title: 'Option 2: Create from a Proposal or Invoice',
      },
      {
        type: 'step',
        title: 'Step 1: Navigate to Proposals or Invoices Tab',
        text: 'Navigate to the project and open the proposal or invoice you would like to convert into a purchase order.',
        image: '/support-images/27.jpg',
      },
      {
        type: 'step',
        title: 'Step 2: Select "Convert to Purchase Order"',
        text: 'From the Actions menu, select Convert to Purchase Order. InteriorFolio will automatically pull over:',
        bullets: [
          'Product selections',
          'Vendor information',
          'Quantities',
          'Pricing',
          'Freight details',
          'Room assignments',
          'Tags and project information',
        ],
        image: '/support-images/28.png',
      },
    ],
  },

  {
    slug: 'managing-settings',
    title: 'Managing Settings',
    intro:
      'The Settings page is your central hub for managing your account, team, integrations, and organizational preferences.',
    blocks: [
      {
        type: 'step',
        title: 'Step 1: Open Your Profile Menu',
        text: 'Navigate to the top right corner of your dashboard and click on your Profile Icon.',
        image: '/support-images/29.png',
      },
      {
        type: 'step',
        title: 'Step 2: Select "Settings"',
        text: 'From the dropdown menu, select Settings to open your account configuration page. InteriorFolio allows you to update and manage:',
        bullets: [
          'My Company details',
          'My Team members and permissions',
          'Tax Locations and rates',
          'Time Billing preferences',
          'Sales Account configurations',
          'QuickBooks integration settings',
          'Tags for project organization',
        ],
      },
    ],
  },

  {
    slug: 'updating-company-details',
    title: 'Updating Company Details',
    intro:
      'Keep your company information up to date so it automatically populates throughout your client-facing documents.',
    blocks: [
      {
        type: 'step',
        title: 'Step 1: Select the "My Company" Tab',
        text: 'From the Settings page, ensure you are on the My Company tab.',
      },
      {
        type: 'step',
        title: 'Step 2: Enter Your Business Information',
        text: 'Fill in the text fields with your current business details. InteriorFolio will automatically pull these details into your:',
        bullets: [
          'Client Proposals',
          'Invoices',
          'Purchase Orders',
          'Payment Requests',
        ],
        note: 'Company information includes: Company Name, Address, Contact Number, Email, Website, and EIN.',

      },
      {
        type: 'step',
        title: 'Step 3: Upload Your Company Logo',
        text: 'Click on the logo upload box on the right side of the screen to upload your business logo. Your uploaded branding will automatically appear across your client-facing documents including:',
        bullets: ['Proposals', 'Invoices', 'Purchase Orders', 'PDFs'],
      },
      {
        type: 'step',
        title: 'Step 4: Save Your Changes',
        text: 'Once your information has been updated, select Save to apply your company settings throughout InteriorFolio.',
        image: '/support-images/30.png',
      },
    ],
  },

  {
    slug: 'managing-team',
    title: 'Managing Your Team',
    intro:
      'Invite team members, set their roles, and manage access permissions all from the My Team tab in Settings.',
    blocks: [
      {
        type: 'step',
        title: 'Step 1: Select the "My Team" Tab',
        text: 'From the Settings page, click on the My Team tab to view your current team roster.',
      },
      {
        type: 'step',
        title: 'Step 2: Add a New Team Member',
        text: 'To invite a new user to your account, click the green + Add New button in the top right corner of the section.',
      },
      {
        type: 'step',
        title: 'Step 3: Edit an Existing User',
        text: "To modify a team member's role, permissions, or cost rate, look under the Actions column and click the green Pencil Icon on their row. InteriorFolio allows you to update:",
        bullets: ['Name', 'Role', 'Cost Rate', 'Start Date', 'Location'],
      },
      {
        type: 'step',
        title: 'Step 4: Remove a Team Member',
        text: 'To remove a user\'s access, look under the Actions column and click the red Remove User Icon (person with an "x") next to the pencil icon on their row.',
      },
      {
        type: 'step',
        title: 'Step 5: Save Your Changes',
        text: 'Once updates have been completed, select Save or Update to apply changes to your team settings and permissions throughout InteriorFolio.',
        image: '/support-images/31.jpg',
      },
    ],
  },

  {
    slug: 'editing-team-members',
    title: 'Editing Team Member Profiles',
    intro:
      'Update employee details, configure access permissions, set billing rates, and assign project access all from the team member profile.',
    blocks: [
      {
        type: 'step',
        title: 'Step 1: Open the Employee Info Tab',
        text: 'When adding or editing a team member, ensure you are on the Employee Info tab to manage their personal and professional details.',
      },
      {
        type: 'step',
        title: 'Step 2: Update Profile and Contact Information',
        text: "Fill out or edit the user's primary identification and contact fields. InteriorFolio will save the following details for the user:",
        bullets: ['First Name', 'Last Name', 'Phone Number', 'Email Address'],
      },
      {
        type: 'step',
        title: 'Step 3: Add Address and Employment Details',
        text: "Enter the team member's operational location, internal designation, and compensation schedules. InteriorFolio tracking includes:",
        bullets: [
          'Street, City, State, and Zip Code',
          'Role',
          'Start Date',
          'Employee Rate (Internal cost rate per hour)',
          'Client Rate (Billable rate per hour charged to projects)',
        ],
      },
      {
        type: 'step',
        title: 'Step 4: Configure Permissions and Project Access',
        text: 'Use the adjacent sub-tabs next to Employee Info to manage exactly what this team member can see and do in the system:',
        bullets: [
          'Grant Access — Define global system permissions, account roles, and administrative privileges.',
          'Project Access — Assign the specific client projects this team member is allowed to view, track time against, or manage.',
        ],
      },
      {
        type: 'step',
        title: 'Step 5: Save Changes',
        text: 'Once all updates are made, click the green Update button in the bottom right corner. To discard changes and return to the main list, click Cancel or the Back to Team link above the sub-tabs.',
        image: '/support-images/32.jpg',
      },
    ],
  },

  {
    slug: 'managing-tax-locations',
    title: 'Managing Tax Locations',
    intro:
      'Set up tax rates by location so they automatically apply to your proposals, invoices, and purchase orders.',
    blocks: [
      {
        type: 'step',
        title: 'Step 1: Open the Tax Location Form',
        text: 'From the Tax Locations tab, click the + Add New button to create a new rate, or click the green Pencil Icon next to an existing location to update it.',
      },
      {
        type: 'step',
        title: 'Step 2: Fill Out Tax Details',
        text: 'Enter the regional jurisdictions and specific tax parameters required for your projects. InteriorFolio will require the following information:',
        bullets: [
          'City',
          'State',
          'Sales Tax',
          'Agency',
          'Updated (Effective date for the rate)',
          'QuickBooks Tax Code (Connect QuickBooks to auto-create tax codes)',
        ],
      },
      {
        type: 'step',
        title: 'Step 3: Save Tax Location',
        text: 'Click the green Save button to apply the tax location to your account settings. To cancel without saving, click the Cancel button.',
        image: '/support-images/33.png',
      },
      {
        image: '/support-images/34.png',
      },
    ],
  },

  {
    slug: 'managing-time-billing',
    title: 'Managing Time Billing Activities',
    intro:
      'Configure the types of billable work your team tracks so they appear as options when logging time.',
    blocks: [
      {
        type: 'step',
        title: 'Step 1: Select the "Time Billing" Tab',
        text: 'From the Settings page, click on the Time Billing tab to manage your billable task types.',
      },
      {
        type: 'step',
        title: 'Step 2: Add a New Billing Activity',
        text: 'To create a new type of billable work, click the green + Add Activity button in the top right corner of the section.',
      },
      {
        type: 'step',
        title: 'Step 3: Edit an Existing Activity',
        text: "To change the name or classification of a current task, look under the Actions column and click the grey Edit button on that activity's row.",
      },
      {
        type: 'step',
        title: 'Step 4: Delete a Billing Activity',
        text: 'To permanently remove a task type from your active options, look under the Actions column and click the red Delete button next to the edit button on its row.',
        image: '/support-images/35.png',
      },
      {
        image: '/support-images/36.jpg',
      },
    ],
  },

  {
    slug: 'managing-tags',
    title: 'Managing Project Tags',
    intro:
      'Tags help you organize and filter proposals, invoices, purchase orders, and products across your projects.',
    blocks: [
      {
        type: 'step',
        title: 'Step 1: Select the "Tags" Tab',
        text: 'From the Settings page, click on the Tags tab to manage your custom organization keywords.',
      },
      {
        type: 'step',
        title: 'Step 2: Add a New Tag',
        text: 'To create a new tag for categorizing your workflows or items, click the green + Add Tag button (visible on the main Tags screen).',
      },
      {
        type: 'step',
        title: 'Step 3: Fill Out Tag Details',
        text: "Give your tag a clear, identifiable name to help streamline your team's filtering and organization. InteriorFolio will save the following details for the tag:",
        bullets: ['Tag Name', 'Color'],
      },
      {
        type: 'step',
        title: 'Step 4: Save or Edit Existing Tags',
        text: "Click the green Save button to add the tag to your master list. To make adjustments later, use the Edit action on the specific tag's row to update its name or properties.",
        image: '/support-images/37.png',
      },
      {
        image: '/support-images/38.png',
      }
    ],
  },

  {
    slug: 'products-services',
    title: 'Products & Services',
    intro:
      'Your Product Library is the foundation of InteriorFolio. Build out your catalog once and pull items into any proposal, invoice, or purchase order.',
    blocks: [
      {
        type: 'step',
        title: 'Step 1: Navigate to Products & Services',
        text: 'From the left navigation menu, select Products & Services.',
      },
      {
        type: 'step',
        title: 'Step 2: Select "Add New"',
        text: 'Click the green + Add New button to create a new product or service item.',
      },
      {
        type: 'step',
        title: 'Step 3: Enter Product Information',
        text: 'InteriorFolio allows you to track:',
        bullets: [
          'Product Name',
          'Vendor',
          'Sales Code',
          'Category',
          'Trade Price',
          'Markup',
          'Dimensions',
          'Colors, Finishes & Materials',
          'Notes',
        ],
      },
      {
        type: 'step',
        title: 'Step 4: Upload Product Images',
        text: 'Upload product images to keep your library visually organized and client-ready.',
      },
      {
        type: 'step',
        title: 'Step 5: Organize with Categories',
        text: 'Organize products using:',
        bullets: ['Product Categories', 'Vendor Filters'],
      },
      {
        type: 'step',
        title: 'Step 6: Save Product',
        text: 'Select Save or Save & Close to add the item to your product library.',
        image: '/support-images/39.png',
      },
    ],
  },
];

export function getArticle(slug) {
  return ARTICLES.find((a) => a.slug === slug) || null;
}
